import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { UserService } from '../../services/user.service';
import { ApplicationService } from '../../services/application.service';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { IntlTelInputComponent } from 'intl-tel-input/angularWithUtils';
import { LogoComponent } from "src/app/components/logo/logo.component";
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { UserProfileAvatarComponent } from "../../components/user-profile-avatar/user-profile-avatar.component";
import { TokenService } from '../../services/token.service';

/**
 * UserApplicationComponent
 * * Handles the comprehensive editing and viewing of a specific user application.
 * Responsibilities:
 * - Loading application data by User ID.
 * - Mapping API data to a complex, multi-section form.
 * - Managing dynamic form arrays (Household Members).
 * - Validating required fields and logic (e.g. income ranges).
 * - Submitting updates or creating new applications via the API.
 */
@Component({
  selector: 'app-user-application',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, NzSkeletonModule, IntlTelInputComponent, LogoComponent, NgxMaskDirective, UserProfileAvatarComponent],
  providers: [provideNgxMask()],
  templateUrl: './user-application.component.html',
  styles: [`
    @import url('https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap');
    main::-webkit-scrollbar { width: 6px; }
    main::-webkit-scrollbar-track { background: #f1f1f1; }
    main::-webkit-scrollbar-thumb { background: #d4d4d8; border-radius: 10px; }
    ::ng-deep .iti { width: 100%; }
    ::ng-deep .iti__input { 
      @apply w-full bg-transparent border-none text-sm focus:outline-none focus:ring-0 transition-all; 
      height: 100%;
    }
    ::ng-deep .iti__tel-input { 
    background: rgb(249 250 251 / var(--tw-bg-opacity, 1)) !important;
    outline-color: transparent !important;
    }
    ::ng-deep .iti__search-input {
      height: 32px !important;
    }
    .signature-font {
      font-family: 'Great Vibes', cursive;
      font-size: 1.5rem;
    }
  `]
})
export class UserApplicationComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private userService = inject(UserService);
  private appService = inject(ApplicationService);
  private notification = inject(NzNotificationService);
  private tokenService = inject(TokenService);

  // --- UI Signals ---
  /** Shows skeleton loader while data fetches */
  isLoading = signal(true);
  /** Disables save button during API submit */
  isSaving = signal(false);
  /** Toggles form disabled state based on app status */
  isReadOnly = signal(false);

  // --- Context State ---
  /** ID of the user whose application we are editing */
  targetUserId: string = '';
  /** The full user object fetched from API */
  targetUser: any = null;
  /** The specific application ID (if one exists) */
  applicationId: string | null = null;

  // --- Form Model ---
  /**
   * Massive form object mirroring the application structure.
   */
  form = {
    status: 'Draft',
    // Added field for update request notes
    updateRequestNotes: '',
    submitToStateOnSave: false,
    statusHistory: [] as any[],
    type: 'My Self',
    zipcode: '',
    householdMembers: [] as any[],
    homeAddress1: '',
    homeAddress2: '',
    city: '',
    homeState: 'NJ',
    mailingIsSameAsHome: true,
    mailingAddress1: '',
    mailingAddress2: '',
    mailingCity: '',
    mailingState: '',
    mailingZipCode: '',
    primaryPhone: '',
    primaryPhoneType: 'MOBILE',
    secondaryPhone: '',
    secondaryPhoneType: '',
    email: '',
    grossAnnualIncome: '',
    medicareCredits: '',
    householdAssets: '',
    weeklyWorkHours: '',
    taxFilingStatus: '',
    smoking: false,
    pregnancy: false,
    isDisabled: false,
    isBlind: false,
    isDiabetic: false,
    isPostpartumPeriod: false,
    hasMoodDisorder: false,
    hasBrainSpinalInjury: false,
    hasLifeLimitingIllness: false,
    hasVehicle: false,
    hasMilitaryAffiliation: false,
    hasBreastCervicalCancer: false,
    hasHIVAIDSDiagnosis: false,
    hasGeneticDisorder: false,
    hasReproductiveHealthNeeds: false,
    hasSubstanceUseDisorder: false,
    isStudent: false,
    participatesInResearch: false,
    hasRehabilitationNeed: false,
    isInFosterCare: false,
    hasTribalMembership: false,
    hasCurrentInsurance: false,
    hasMedicare: false,
    qualifyingLifeEvent: '',
    healthConditions: [] as string[],
    attestationAgreed: false,
    electronicSignature: '',
    attestationDate: new Date().toISOString().split('T')[0]
  };

  // --- Static Option Lists ---
  usStates = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
  statusOptions = ['Draft', 'Pre-Submission Review', 'Awaiting Applicant Updates'];
  maritalStatuses = ['SINGLE', 'MARRIED', 'DIVORCED', 'WIDOWED', 'SEPARATED'];
  citizenshipOptions = [
    { value: 'US_CITIZEN', label: 'U.S. Citizen' },
    { value: 'NATURALIZED_CITIZEN', label: 'Naturalized Citizen' },
    { value: 'PERMANENT_RESIDENT', label: 'Permanent Resident' },
    { value: 'OTHER', label: 'Other' }
  ];
  ssnStatusOptions = [
    { value: 'haveSSN', label: 'I have SSN' },
    { value: 'noSSN', label: 'I don\'t have SSN' },
    { value: 'applied', label: 'Applied for SSN' }
  ];
  suffixOptions = ['Jr', 'Sr', 'II', 'III', 'IV'];
  phoneTypes = ['MOBILE', 'HOME', 'WORK', 'OTHER'];
  lifeEvents = ['Married', 'Divorced', 'Loss of Coverage', 'Moved', 'Changed or Lost Job', 'Birth or Adoption', 'None of the above'];
  healthConditionOptions = ['Cancer', 'Heart Attack', 'Stroke', 'Diabetes', 'AIDS / HIV', 'Pulmonary Disease', 'None of the above'];

  /**
   * Lifecycle Hook:
   * Retrieves userId from route parameters and initiates data loading.
   */
  ngOnInit() {
    this.targetUserId = this.route.snapshot.paramMap.get('userId') || '';
    if (this.targetUserId) {
      this.loadTargetUserData();
    } else {
      this.notification.error('Error', 'No User ID provided');
      this.router.navigate(['/intake']);
    }
  }

  /**
   * Fetches the user entity by ID.
   * If the user has applications, picks the latest one to edit.
   * If not, initializes a default form.
   */
  loadTargetUserData() {
    this.isLoading.set(true);
    this.userService.getUserById(this.targetUserId).subscribe({
      next: (user) => {
        this.targetUser = user;
        const apps = user.applications?.filter((a: any) => !a.isDeleted);

        if (apps && apps.length > 0) {
          // Sort to find the latest
          apps.sort((a: any, b: any) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
          this.mapApiToForm(apps[0]);
        } else {
          // No apps found, set defaults
          this.initializeDefaultMember(user);
        }
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.notification.error('Error', 'Failed to load user data');
        this.router.navigate(['/intake']);
      }
    });
  }

  /**
   * Logs out the current user.
   */
  logout() {
    this.tokenService.clearToken();
    this.router.navigate(['/login']);
  }

  /**
   * Pre-fills the form with the user's basic contact info and creates
   * a single "Applicant" household member.
   * * @param user - User object.
   */
  initializeDefaultMember(user: any) {
    this.form.status = 'Draft';
    this.form.email = user.email || '';
    this.form.primaryPhone = user.phoneNumber || '';
    this.form.primaryPhoneType = 'MOBILE';

    this.form.householdMembers = [{
      isApplicant: true,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      middleName: '',
      suffix: '',
      dateOfBirth: '',
      gender: '',
      maritalStatus: '',
      ssn: '',
      ssnStatus: 'haveSSN',
      citizenshipStatus: 'US_CITIZEN',
      heightFt: '',
      heightIn: '',
      weightLbs: '',
      isStudent: 'No'
    }];
  }

  /**
   * Maps an existing Application API object to the local form model.
   * Handles boolean conversions, date strings, and complex nested objects.
   * * @param app - The application object from the API.
   */
  mapApiToForm(app: any) {
    this.applicationId = app.id;
    const getVal = (val: any, def: any) => (val !== undefined && val !== null) ? val : def;

    // Status Logic
    this.form.status = getVal(app.status, 'Draft');
    this.form.updateRequestNotes = getVal(app.updateRequestNotes, '');

    const editableStatuses = ['Draft', 'Pre-Submission Review', 'Awaiting Applicant Updates'];

    // Lock form if status is final
    if (this.form.status && !editableStatuses.includes(this.form.status)) {
      this.isReadOnly.set(true);
    }

    this.form.type = getVal(app.type, 'My Self');
    this.form.zipcode = getVal(app.zipcode, '');

    // History
    const history = getVal(app.statusHistory, []);
    this.form.statusHistory = history.sort((a: any, b: any) => {
      return new Date(b.updatedDate).getTime() - new Date(a.updatedDate).getTime();
    });

    // --- Household Members Mapping ---
    const mapMember = (m: any) => {
      let ssnStatus = 'haveSSN';
      if (!m.ssn) ssnStatus = 'noSSN';
      const dob = m.dateOfBirth ? m.dateOfBirth.split('T')[0] : '';
      let isStudentVal = getVal(m.isStudent, 'No');
      if (isStudentVal === true) isStudentVal = 'Yes';
      if (isStudentVal === false) isStudentVal = 'No';

      // Ensure Primary Applicant matches User details
      let finalFirstName = m.firstName;
      let finalLastName = m.lastName;
      if (m.isApplicant && this.targetUser) {
        finalFirstName = this.targetUser.firstName;
        finalLastName = this.targetUser.lastName;
      }
      return {
        ...m,
        firstName: finalFirstName,
        lastName: finalLastName,
        dateOfBirth: dob,
        suffix: m.suffix || '',
        ssnStatus: ssnStatus,
        ssn: m.ssn ? m.ssn : '',
        heightFt: getVal(m.heightFt, ''),
        heightIn: getVal(m.heightIn, ''),
        weightLbs: getVal(m.weightLbs, ''),
        isStudent: isStudentVal
      };
    };

    if (app.householdMembers && app.householdMembers.length > 0) {
      this.form.householdMembers = app.householdMembers.map(mapMember);
    } else {
      // Fallback if array exists but empty (edge case)
      this.form.householdMembers = [{
        isApplicant: true,
        firstName: this.targetUser.firstName,
        lastName: this.targetUser.lastName,
        middleName: app.middleName || '',
        suffix: app.suffix || '',
        dateOfBirth: app.birthday ? app.birthday.split('T')[0] : '',
        gender: app.gender || '',
        maritalStatus: app.maritalStatus || '',
        ssn: app.ssn || '',
        ssnStatus: app.ssn ? 'haveSSN' : 'noSSN',
        citizenshipStatus: app.citizenshipStatus || 'US_CITIZEN',
        heightFt: '',
        heightIn: '',
        weightLbs: '',
        isStudent: 'No'
      }];
    }

    // --- Address & Contact Mapping ---
    this.form.homeAddress1 = getVal(app.homeAddress1, '');
    this.form.homeAddress2 = getVal(app.homeAddress2, '');
    this.form.city = getVal(app.city, '');
    this.form.homeState = getVal(app.homeState, 'NJ');
    this.form.mailingAddress1 = getVal(app.mailingAddress1, '');
    this.form.mailingAddress2 = getVal(app.mailingAddress2, '');
    this.form.mailingCity = getVal(app.mailingCity, '');
    this.form.mailingState = getVal(app.mailingState, '');
    this.form.mailingZipCode = getVal(app.mailingZipCode, '');
    this.form.mailingIsSameAsHome = !this.form.mailingAddress1;
    this.form.primaryPhone = this.targetUser.phoneNumber || '';
    this.form.email = this.targetUser.email || '';
    this.form.primaryPhoneType = 'MOBILE';
    this.form.secondaryPhone = getVal(app.secondaryPhone, '');
    this.form.secondaryPhoneType = getVal(app.secondaryPhoneType, '');

    // --- Financial Mapping ---
    this.form.grossAnnualIncome = getVal(app.grossAnnualIncome || app.houseHoldIncomeRange, '');
    this.form.medicareCredits = getVal(app.medicareCredits, '');
    this.form.householdAssets = getVal(app.householdAssets, '');
    this.form.weeklyWorkHours = getVal(app.weeklyWorkHours, '');
    this.form.taxFilingStatus = getVal(app.taxFilingStatus, '');

    // --- Boolean Fields Mapping ---
    const boolFields = [
      'smoking', 'pregnancy', 'isDisabled', 'isBlind', 'isDiabetic', 'isPostpartumPeriod',
      'hasMoodDisorder', 'hasBrainSpinalInjury', 'hasLifeLimitingIllness', 'hasVehicle',
      'hasMilitaryAffiliation', 'hasBreastCervicalCancer', 'hasHIVAIDSDiagnosis',
      'hasGeneticDisorder', 'hasReproductiveHealthNeeds', 'hasSubstanceUseDisorder',
      'participatesInResearch', 'hasRehabilitationNeed', 'isInFosterCare',
      'hasTribalMembership', 'hasCurrentInsurance', 'hasMedicare', 'attestationAgreed'
    ];
    boolFields.forEach(field => {
      // @ts-ignore
      this.form[field] = !!app[field];
    });

    this.form.qualifyingLifeEvent = getVal(app.qualifyingLifeEvent || app.lifeEvent, '');
    this.form.healthConditions = getVal(app.healthConditions, []);
    this.form.electronicSignature = getVal(app.electronicSignature, '');
  }

  /**
   * Adds a new empty member object to the householdMembers array.
   */
  addMember() {
    this.form.householdMembers.push({
      isApplicant: false, // Only the first is primary
      firstName: '',
      lastName: '',
      middleName: '',
      suffix: '',
      dateOfBirth: '',
      gender: '',
      citizenshipStatus: 'US_CITIZEN',
      ssn: '',
      ssnStatus: 'haveSSN',
      heightFt: '',
      heightIn: '',
      weightLbs: '',
      isStudent: 'No'
    });
  }

  /**
   * Removes a member from the household array.
   * @param index - Index to remove.
   */
  removeMember(index: number) {
    this.form.householdMembers.splice(index, 1);
  }

  /**
   * Calculates 'Is Student' status based on Date of Birth.
   * If age is between 5 and 24, defaults to 'Yes'.
   * * @param member - The member object to update.
   */
  checkStudentStatus(member: any) {
    if (!member.dateOfBirth) return;
    const dob = new Date(member.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    if (age >= 5 && age <= 24) {
      member.isStudent = 'Yes';
    } else {
      member.isStudent = 'No';
    }
  }

  /**
   * Strips formatting from SSN input to store only digits.
   */
  private cleanSSN(ssn: string): string {
    if (!ssn) return '';
    return ssn.replace(/\D/g, '');
  }

  /**
   * Legacy Helper: Maps specific income values to string ranges for old APIs.
   * * @param incomeVal - Numeric income string.
   * @returns Range string (e.g. '12000-28999').
   */
  private determineIncomeRange(incomeVal: any): string {
    const income = parseFloat(incomeVal);
    if (isNaN(income)) return 'below12000';
    if (income >= 47000) return '47000+';
    if (income >= 29000) return '29000-46999';
    if (income >= 12000) return '12000-28999';
    return 'below12000';
  }

  handlePrimaryPhoneChange(event: any) { } // Placeholder
  handleSecondaryPhoneChange(event: any) {
    this.form.secondaryPhone = event;
  }

  /**
   * Toggles a health condition in the selected array.
   * Handles "None of the above" exclusivity logic.
   * * @param condition - The condition string toggled.
   */
  toggleCondition(condition: string) {
    const idx = this.form.healthConditions.indexOf(condition);
    if (idx > -1) {
      // Remove if exists
      this.form.healthConditions.splice(idx, 1);
    } else {
      // Add if new
      if (condition === 'None of the above') {
        // Clear others if None selected
        this.form.healthConditions = ['None of the above'];
      } else {
        // Remove None if specific condition selected
        const noneIdx = this.form.healthConditions.indexOf('None of the above');
        if (noneIdx > -1) this.form.healthConditions.splice(noneIdx, 1);
        this.form.healthConditions.push(condition);
      }
    }
  }

  /**
   * Submits the form data to create or update the application.
   * 1. Validates form.
   * 2. Sanitizes inputs (SSN cleaning, number conversion).
   * 3. Constructs payload matching API schema.
   * 4. Calls Service.
   */
  saveApplication() {
    if (!this.validateForm()) return;

    this.isSaving.set(true);
    const applicant = this.form.householdMembers[0];

    // Clean up members data
    const cleanedMembers = this.form.householdMembers.map(m => {
      let finalSSN = '';
      if (m.ssnStatus === 'haveSSN') {
        finalSSN = this.cleanSSN(m.ssn);
      }
      return {
        ...m,
        ssn: finalSSN,
        isStudent: m.isStudent === 'Yes'
      };
    });

    const toNum = (val: any) => (val === '' || val === null || val === undefined) ? null : Number(val);
    const toStr = (val: any) => (val === '' || val === null || val === undefined) ? null : String(val);

    const grossMonthlyIncome = this.form.grossAnnualIncome
      ? (parseFloat(this.form.grossAnnualIncome) / 12).toFixed(2)
      : '0';

    let finalStatus = this.form.status;
    if (this.form.submitToStateOnSave) {
      finalStatus = 'Submitted to State';
    }

    const payload = {
      userId: this.targetUserId,
      type: this.form.type,
      zipcode: this.form.zipcode,
      householdSize: this.form.householdMembers.length.toString(),
      status: finalStatus,
      updateRequestNotes: this.form.updateRequestNotes, // Include notes in payload
      updatedDate: new Date().toISOString(),
      firstName: applicant.firstName,
      lastName: applicant.lastName,
      middleName: applicant.middleName,
      suffix: applicant.suffix,
      gender: applicant.gender,
      birthday: applicant.dateOfBirth ? new Date(applicant.dateOfBirth).toISOString() : null,
      maritalStatus: applicant.maritalStatus,
      ssn: cleanedMembers[0].ssn,
      citizenshipStatus: applicant.citizenshipStatus,
      heightFeet: toNum(applicant.heightFt),
      heightInch: toNum(applicant.heightIn),
      weight: toNum(applicant.weightLbs),
      householdMembers: cleanedMembers,
      healthConditions: this.form.healthConditions,
      homeAddress1: this.form.homeAddress1,
      homeAddress2: this.form.homeAddress2,
      city: this.form.city,
      homeState: this.form.homeState,
      mailingAddress1: this.form.mailingIsSameAsHome ? '' : this.form.mailingAddress1,
      mailingAddress2: this.form.mailingIsSameAsHome ? '' : this.form.mailingAddress2,
      mailingCity: this.form.mailingIsSameAsHome ? '' : this.form.mailingCity,
      mailingState: this.form.mailingIsSameAsHome ? '' : this.form.mailingState,
      mailingZipCode: this.form.mailingIsSameAsHome ? '' : this.form.mailingZipCode,
      primaryPhone: this.form.primaryPhone,
      primaryPhoneType: this.form.primaryPhoneType,
      secondaryPhone: this.form.secondaryPhone,
      secondaryPhoneType: this.form.secondaryPhoneType,
      email: this.form.email,
      grossAnnualIncome: toStr(this.form.grossAnnualIncome),
      houseHoldIncomeRange: this.determineIncomeRange(this.form.grossAnnualIncome),
      grossMonthlyIncome: grossMonthlyIncome,
      medicareCredits: toStr(this.form.medicareCredits),
      householdAssets: toStr(this.form.householdAssets),
      weeklyWorkHours: toStr(this.form.weeklyWorkHours),
      taxFilingStatus: this.form.taxFilingStatus,
      childrenAges: '',
      olderAdultsAges: '',
      eligibilityResult: null,
      smoking: this.form.smoking,
      pregnancy: this.form.pregnancy,
      isDisabled: this.form.isDisabled,
      isBlind: this.form.isBlind,
      isDiabetic: this.form.isDiabetic,
      isPostpartumPeriod: this.form.isPostpartumPeriod,
      hasMoodDisorder: this.form.hasMoodDisorder,
      hasBrainSpinalInjury: this.form.hasBrainSpinalInjury,
      hasLifeLimitingIllness: this.form.hasLifeLimitingIllness,
      hasVehicle: this.form.hasVehicle,
      hasMilitaryAffiliation: this.form.hasMilitaryAffiliation,
      hasBreastCervicalCancer: this.form.hasBreastCervicalCancer,
      hasHIVAIDSDiagnosis: this.form.hasHIVAIDSDiagnosis,
      hasGeneticDisorder: this.form.hasGeneticDisorder,
      hasReproductiveHealthNeeds: this.form.hasReproductiveHealthNeeds,
      hasSubstanceUseDisorder: this.form.hasSubstanceUseDisorder,
      isStudent: cleanedMembers.some(m => m.isStudent === 'Yes'),
      participatesInResearch: this.form.participatesInResearch,
      hasRehabilitationNeed: this.form.hasRehabilitationNeed,
      isInFosterCare: this.form.isInFosterCare,
      hasTribalMembership: this.form.hasTribalMembership,
      hasCurrentInsurance: this.form.hasCurrentInsurance,
      hasMedicare: this.form.hasMedicare,
      qualifyingLifeEvent: this.form.qualifyingLifeEvent,
      lifeEvent: this.form.qualifyingLifeEvent,
      attestationAgreed: this.form.attestationAgreed,
      electronicSignature: this.form.electronicSignature,
    };

    // Determine Create or Update operation
    const request$ = this.applicationId
      ? this.appService.updateApplication(this.applicationId, payload)
      : this.appService.createApplication(payload);

    request$.subscribe({
      next: () => {
        this.isSaving.set(false);
        this.notification.success('Success', 'Application details saved successfully.');
        this.cancel();
      },
      error: (err) => {
        console.error('Save failed', err);
        this.isSaving.set(false);
        this.notification.error('Error', 'Failed to save application.');
      }
    });
  }

  /**
   * Performs client-side validation on the form model.
   * Returns false and shows a toast error if validation fails.
   */
  validateForm(): boolean {
    const zipRegex = /^\d{5}$/;

    // Check specific requirement: Notes are mandatory if status is 'Awaiting Applicant Updates'
    if (this.form.status === 'Awaiting Applicant Updates' && !this.form.updateRequestNotes?.trim()) {
      this.notification.error('Validation Error', 'Update notes are required when status is "Awaiting Applicant Updates". Please explain what the applicant needs to change.');
      return false;
    }

    if (!this.form.zipcode || !zipRegex.test(this.form.zipcode)) {
      this.notification.error('Validation Error', 'Please enter a valid 5-digit Application Zip Code.');
      return false;
    }
    if (!this.form.homeAddress1) {
      this.notification.error('Validation Error', 'Home Street Address 1 is required.');
      return false;
    }
    if (!this.form.city) {
      this.notification.error('Validation Error', 'Home City is required.');
      return false;
    }
    if (!this.form.homeState) {
      this.notification.error('Validation Error', 'Home State is required.');
      return false;
    }
    // Loop through all members for validation
    for (const [index, member] of this.form.householdMembers.entries()) {
      const memberLabel = member.isApplicant ? 'Primary Applicant' : `Member ${index + 1}`;
      if (!member.firstName) {
        this.notification.error('Validation Error', `${memberLabel}: First Name is required.`);
        return false;
      }
      if (!member.lastName) {
        this.notification.error('Validation Error', `${memberLabel}: Last Name is required.`);
        return false;
      }
      if (!member.dateOfBirth) {
        this.notification.error('Validation Error', `${memberLabel}: Date of Birth is required.`);
        return false;
      }
      if (!member.gender) {
        this.notification.error('Validation Error', `${memberLabel}: Gender is required.`);
        return false;
      }
      if (!member.maritalStatus) {
        this.notification.error('Validation Error', `${memberLabel}: Marital Status is required.`);
        return false;
      }
      if (!member.citizenshipStatus) {
        this.notification.error('Validation Error', `${memberLabel}: Citizenship Status is required.`);
        return false;
      }
      if (!member.isStudent) {
        this.notification.error('Validation Error', `${memberLabel}: Student status is required.`);
        return false;
      }
    }
    if (!this.form.grossAnnualIncome) {
      this.notification.error('Validation Error', 'Gross Annual Household Income is required.');
      return false;
    }
    if (!this.form.qualifyingLifeEvent) {
      this.notification.error('Validation Error', 'Please select a Qualifying Life Event.');
      return false;
    }
    if (!this.form.healthConditions || this.form.healthConditions.length === 0) {
      this.notification.error('Validation Error', 'Please select at least one Health Condition (or "None of the above").');
      return false;
    }
    if (!this.form.attestationAgreed) {
      this.notification.error('Validation Error', 'You must agree to the attestation statement.');
      return false;
    }
    if (!this.form.electronicSignature) {
      this.notification.error('Validation Error', 'Please provide an Electronic Signature.');
      return false;
    }
    return true;
  }

  /**
   * Navigates back to the main intake dashboard.
   */
  cancel() {
    this.router.navigate(['/intake']);
  }
}